import { NavLink } from "react-router-dom";
import { HelpCircle, Home, Lightbulb, type LucideIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/** A route link — highlights active via NavLink. */
export type RailLinkItem = {
  kind: "link";
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
};

/** A plain click action — no route, e.g. focus the search bar. */
export type RailActionItem = {
  kind: "action";
  label: string;
  icon: LucideIcon;
  onClick: () => void;
};

/** Fully custom rendered node — e.g. the live notification bell. */
export type RailCustomItem = {
  kind: "custom";
  key: string;
  render: () => React.ReactNode;
};

export type RailNavItem = RailLinkItem | RailActionItem | RailCustomItem;

type Props = {
  brandTo: string;
  primaryNav: RailNavItem[];
  userMenu: React.ReactNode;
  helpHref?: string;
  featureRequestHref?: string;
  /** When provided, the Help icon opens the in-app widget instead of a mailto link. */
  onOpenHelp?: () => void;
  /** When provided, the Lightbulb opens the Message-us form preset to Feature request. */
  onOpenFeatureRequest?: () => void;
};

// Wrapper: layout/sizing ONLY, no color, no background box. "group" lets the
// icon (below) react to hover on this wrapper regardless of element type.
// Exported so NotificationBell renders with the exact same slot/icon classes.
export const RAIL_SLOT = "group flex h-11 w-11 shrink-0 items-center justify-center rounded-xl";
// Color lives directly on the icon glyph, not inherited from the wrapper —
// avoids any cross-element (button vs a) currentColor inheritance mismatch.
// On hover, just darkens (text-foreground) with a smooth color transition —
// no background box, no scale/transform.
export const RAIL_ICON = "h-5 w-5 text-muted-foreground transition-colors group-hover:text-foreground";

function RailItem(item: RailNavItem) {
  if (item.kind === "custom") return item.render();

  if (item.kind === "action") {
    const Icon = item.icon;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={item.label} onClick={item.onClick} className={RAIL_SLOT}>
            <Icon className={RAIL_ICON} strokeWidth={1.75} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  const Icon = item.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink to={item.to} end={item.end} aria-label={item.label} className={RAIL_SLOT}>
          <Icon className={RAIL_ICON} strokeWidth={1.75} />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export function SidebarRail({
  brandTo,
  primaryNav,
  userMenu,
  helpHref = "mailto:support@editlock.io",
  featureRequestHref = "mailto:support@editlock.io?subject=Feature%20request",
  onOpenHelp,
  onOpenFeatureRequest,
}: Props) {
  return (
    <aside
      className="sticky top-0 z-40 flex h-screen w-[76px] shrink-0 flex-col items-center border-r border-border bg-sidebar py-4"
      aria-label="Primary"
    >
      {/* Top cluster: brand/home + primary nav — one uniform gap, no dividers */}
      <div className="flex flex-col items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink to={brandTo} end aria-label="Home" className={RAIL_SLOT}>
              <Home className={RAIL_ICON} strokeWidth={1.75} />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right">Home</TooltipContent>
        </Tooltip>

        {primaryNav.map((item) => (
          <RailItem key={item.kind === "custom" ? item.key : item.kind === "link" ? item.to : item.label} {...item} />
        ))}
      </div>

      <div className="flex-1" />

      {/* Bottom cluster: feature request, help, account menu — same uniform gap */}
      <div className="flex flex-col items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {onOpenFeatureRequest ? (
              <button type="button" onClick={onOpenFeatureRequest} aria-label="Submit feature request" className={RAIL_SLOT}>
                <Lightbulb className={RAIL_ICON} strokeWidth={1.75} />
              </button>
            ) : (
              <a href={featureRequestHref} aria-label="Submit feature request" className={RAIL_SLOT}>
                <Lightbulb className={RAIL_ICON} strokeWidth={1.75} />
              </a>
            )}
          </TooltipTrigger>
          <TooltipContent side="right">Submit feature request</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            {onOpenHelp ? (
              <button type="button" onClick={onOpenHelp} aria-label="Help & support" className={RAIL_SLOT}>
                <HelpCircle className={RAIL_ICON} strokeWidth={1.75} />
              </button>
            ) : (
              <a href={helpHref} aria-label="Help & support" className={RAIL_SLOT}>
                <HelpCircle className={RAIL_ICON} strokeWidth={1.75} />
              </a>
            )}
          </TooltipTrigger>
          <TooltipContent side="right">Help &amp; support</TooltipContent>
        </Tooltip>

        {userMenu}
      </div>
    </aside>
  );
}
